import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { joinClass } from '../../lib/zoom-join';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Calendar, Clock, Loader2, ShieldCheck, Sparkles, CheckCircle2, Video } from 'lucide-react';
import { toast } from 'sonner';
import { TrialAssessment } from '../../components/learner/TrialAssessment';
import { CoachOnboarding } from '../../components/prenatal/CoachOnboarding';

// V27 — Free / SoulAI-paid learners: upcoming live prenatal yoga classes matched to risk + country,
// pay the trial (₹99 / $) for the class they pick; the team connects within 24 hrs to confirm.
export default function TrialClasses() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(null);
  const [joinBuffer, setJoinBuffer] = useState(5);

  const load = useCallback(() => api.get('/api/trial/upcoming-classes').then((r) => setData(r.data))
    .catch(() => toast.error('Could not load classes')).finally(() => setLoading(false)), []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/settings/buffers').then((r) => setJoinBuffer(r.data?.learner_join_buffer ?? 5)).catch(() => {}); }, []);

  // Same join window the Elite learner gets on My Classes.
  const joinOpen = (cls) => {
    if (!cls?.scheduled_date || !cls?.scheduled_time) return false;
    const mins = (new Date(`${cls.scheduled_date}T${cls.scheduled_time}:00+05:30`) - new Date()) / 60000;
    return mins <= joinBuffer && mins > -120;
  };

  const submitRequest = async (session, orderId) => {
    try {
      const r = await api.post('/api/trial/request', { session_id: session.session_id, payment_order_id: orderId });
      setDone(r.data);
      if (refreshUser) refreshUser();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not send your request');
    }
  };

  const startPayment = async (session) => {
    setPaying(true);
    try {
      const q = (await api.get(data?.payment?.catalog || '/api/pricing/catalog?placement=trial_class&stage=prenatal')).data;
      const item = q.items?.trial;
      const order = (await api.post('/api/payment/create-order', { quote_id: q.quote_id, method: 'razorpay', item_key: 'trial' })).data;
      const key = order.razorpay_key_id || q.razorpay_key_id;
      if (!order.order_id || !key || !window.Razorpay) { toast.error('Payment gateway unavailable. Please retry.'); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key, amount: item.razorpay_amount_paise, currency: item.razorpay_currency || q.currency,
        name: 'Soul Sanskar Academy', description: `Trial class · ${session.title || program}`, order_id: order.order_id,
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
        theme: { color: '#4A6741' },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (resp) => {
          try {
            const v = await api.post('/api/payment/verify', { order_id: order.order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature });
            if (v.data.verified) { toast.success('Payment received'); await submitRequest(session, order.order_id); }
            else toast.error('Payment verification failed. Contact support.');
          } catch { toast.error('Payment verification error. Contact support if charged.'); }
          setPaying(false);
        },
      });
      rzp.open();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Payment could not be started');
      setPaying(false);
    }
  };

  // Founder (11 Sep): no questions before payment — Book → pay → "team connects within 24 hrs"
  const onPick = async (s) => {
    setPicked(s);
    if (data?.trial_paid) { await submitRequest(s, null); return; }
    await startPayment(s);
  };

  if (loading) return <div className="p-8 flex justify-center" data-testid="trial-classes-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data?.eligible) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="trial-classes-ineligible">
        <Card><CardContent className="p-6 text-sm text-muted-foreground">
          {data?.reason === 'ELITE_USE_BOOK_TAB' ? <>You're an Elite member — book directly from <button className="underline" onClick={() => navigate('/learner/book')}>Book Classes</button>.</> : 'Live trial classes for your stage are coming soon.'}
        </CardContent></Card>
      </div>
    );
  }

  const program = data.program_name || 'Prenatal Yoga';
  const req = done?.request || data.my_request;

  // V65 (founder, 7 Oct 2026) — once the trial is paid, she never picks her own slot again:
  // no batches, no timings, no Book. The team books / reschedules / cancels from the backend.
  if (data.self_booking_allowed === false || done) {
    const cls = data.confirmed_class;
    return (
      <div className="space-y-6 p-4 sm:p-6 max-w-3xl mx-auto" data-testid="trial-classes-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold" data-testid="trial-page-title">Live {program} — your trial class</h1>
        </div>
        {cls ? (
          <Card className="border-emerald-300 bg-emerald-50/60" data-testid="trial-confirmed-class">
            <CardContent className="p-5 space-y-4">
              <div className="flex gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">Your trial class is booked</p>
                  <p className="text-muted-foreground mt-1">{cls.title || program}</p>
                  <p className="text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(`${cls.scheduled_date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
                    <Clock className="h-3.5 w-3.5 ml-2" />{cls.scheduled_time} · {cls.duration} min
                  </p>
                  {cls.instructor_name && <p className="text-muted-foreground mt-0.5">With {cls.instructor_name}</p>}
                </div>
              </div>
              <Button className="w-full" disabled={!joinOpen(cls)} onClick={() => joinClass(cls.session_id)} data-testid="trial-join-class">
                <Video className="h-4 w-4 mr-2" />{joinOpen(cls) ? 'Join class now' : `Join opens ${joinBuffer} min before your class`}
              </Button>
              <p className="text-xs text-muted-foreground">
                Need a different day or time? Our team handles every change — just reply to their message or call them and they'll reschedule it for you.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-300 bg-emerald-50/60" data-testid="trial-request-status">
            <CardContent className="p-5 flex gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">Request received</p>
                <p className="text-muted-foreground mt-1" data-testid="trial-team-message">{done?.message || data.pending_message || data.team_message}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  if (data.assessment_required && !req) {
    const form = data.onboarding_form;
    return (
      <div className="space-y-6 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="trial-classes-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold" data-testid="trial-page-title">Live {program} — your trial class</h1>
          <p className="text-sm text-muted-foreground mt-1">{form?.intro || 'One quick step so we match you to the right batch.'}</p>
        </div>
        {form ? (
          <CoachOnboarding
            config={form.config}
            edd={form.edd}
            week={form.week}
            stage={form.stage}
            prefill={form.prefill}
            eddLocked={form.edd_locked}
            submitLabel="Save & show my classes"
            onDone={() => { setLoading(true); load(); }}
          />
        ) : (
          <TrialAssessment assessment={data.assessment} onDone={() => { setLoading(true); load(); }} />
        )}
      </div>
    );
  }
  const byDate = data.sessions.reduce((acc, s) => { (acc[s.scheduled_date] ||= []).push(s); return acc; }, {});
  const price = data.trial_price?.formatted || null;

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="trial-classes-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold" data-testid="trial-page-title">Live {program} — book your trial class</h1>
        <p className="text-sm text-muted-foreground mt-1">{data.stage === 'postnatal' ? 'Gentle postnatal recovery classes for new moms.' : 'Classes matched to your pregnancy.'} Pick a slot you prefer and pay the trial fee — our team will get in touch within 24 hours (except Sunday) and book your trial class based on your pregnancy details.</p>
        <div className="flex gap-2 mt-3 flex-wrap">
          {data.stage !== 'postnatal' && (
          <Badge variant="outline" className={data.risk_level === 'high' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'} data-testid="trial-risk-badge">
              <ShieldCheck className="h-3 w-3 mr-1" />{data.risk_level === 'high' ? 'High-risk friendly batch' : 'Low-risk batch'}
            </Badge>
          )}
          {data.trial_price?.formatted && (
            <Badge variant="outline" data-testid="trial-price-badge">
              Trial {data.trial_price.formatted}{data.trial_price.tax_inclusive ? ' · incl. 18% GST' : ''}
            </Badge>
          )}
          {data.trial_paid && <Badge className="bg-emerald-600" data-testid="trial-paid-badge">Trial fee paid</Badge>}
        </div>
      </div>

      {Object.keys(byDate).length === 0 && (
        <Card><CardContent className="p-6 text-sm text-muted-foreground" data-testid="trial-no-sessions">No classes today or tomorrow for your batch yet — please check back soon.</CardContent></Card>
      )}

      {Object.entries(byDate).map(([date, list]) => (
        <section key={date} data-testid={`trial-day-${date}`}>
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2"><Calendar className="h-4 w-4" />{new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {list.map((s) => (
              <Card key={s.session_id} className="hover:border-primary/50 transition-colors" data-testid={`trial-session-${s.session_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.title || program}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{s.scheduled_time} · {s.duration} min{s.instructor_name ? ` · ${s.instructor_name}` : ''}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.batch_label}</p>
                  </div>
                  <Button size="sm" disabled={paying} onClick={() => onPick(s)} data-testid={`trial-book-${s.session_id}`}>
                    {paying && picked?.session_id === s.session_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />{data.trial_paid || !price ? 'Book' : `Book · ${price}`}</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}

    </div>
  );
}
